import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);

const LOCAL_KEY = "duo_cart";

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Load from local on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch { /* noop */ }
    setLoaded(true);
  }, []);

  // Merge with server cart when user logs in
  useEffect(() => {
    const sync = async () => {
      if (!user) return;
      try {
        const { data: serverItems } = await api.get("/me/cart");
        if (serverItems && serverItems.length) {
          // merge local items (if any) with server
          setItems((prev) => {
            const map = new Map();
            [...serverItems, ...prev].forEach((i) => {
              const key = `${i.product_id}-${i.size || ""}-${i.variation || ""}`;
              const existing = map.get(key);
              if (existing) existing.quantity += i.quantity;
              else map.set(key, { ...i });
            });
            return Array.from(map.values());
          });
        }
      } catch { /* noop */ }
    };
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Persist locally
  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem(LOCAL_KEY, JSON.stringify(items));
  }, [items, loaded]);

  // Persist to server when logged in
  useEffect(() => {
    if (!user || !loaded) return;
    api.put("/me/cart", items).catch(() => {});
  }, [items, user, loaded]);

  const addItem = useCallback((product, qty = 1, size = null, variation = null, pair = null) => {
    setItems((prev) => {
      const key = `${product.id}-${size || ""}-${variation || ""}-${pair ? `${pair.size_1}-${pair.name_1}-${pair.size_2}-${pair.name_2}` : ""}`;
      const idx = prev.findIndex((i) => {
        const k = `${i.product_id}-${i.size || ""}-${i.variation || ""}-${i.pair ? `${i.pair.size_1}-${i.pair.name_1}-${i.pair.size_2}-${i.pair.name_2}` : ""}`;
        return k === key;
      });
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          image: (product.images && product.images[0]) || null,
          unit_price: product.sale_price || product.price,
          quantity: qty,
          size, variation, pair,
        },
      ];
    });
  }, []);

  const updateQty = (key, qty) =>
    setItems((prev) =>
      prev
        .map((i) =>
          `${i.product_id}-${i.size || ""}-${i.variation || ""}` === key
            ? { ...i, quantity: Math.max(1, qty) }
            : i
        )
    );

  const removeItem = (key) =>
    setItems((prev) => prev.filter((i) => `${i.product_id}-${i.size || ""}-${i.variation || ""}` !== key));

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
